import React, { useState, useEffect, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { httpsCallable } from 'firebase/functions';
import { captureMessage as sentryCaptureMessage } from '@sentry/react';
import { Box, Button, CircularProgress, Divider, Grid, Typography } from '@mui/material';

import { useAuth } from '../../../hooks';
import { SERVICES } from '../paymentHelpers';
import { functions } from '../../../firebase-config';
import PromoContainer from './PromoContainer';
import PaymentContext from '../PaymentContext';
import { TreatmentTerms } from './TreatmentTerms';
import { McPromoCheckoutDisplay } from './McPromoCheckoutDisplay.tsx';
import {
  convertToCurrency,
  scriptPriceTranslator,
  getProductDetailedName,
  getHealthCategoryDisplayName,
} from '../../../utils';
import { CHECKUP } from '../../../utils/constants';
import { hasActiveMcPromo } from '../../../utils/hasActiveMcPromo';

/**
 * Extended catalogue item type with fields added dynamically by backend
 * when special MC promo is applied
 * @typedef {CatalogueItem & { originalPrice?: number, hasSpecialMcPromo?: boolean }} CatalogueItemWithPromo
 */

/**
 * @type {import('firebase/functions').HttpsCallable<
 *  {
 *  selectedTreatments: string[];
 *  discountCode: string,
 *  userId?: string,
 *  isPickup: boolean,
 *  formCategory?: string
 * },
 *  CheckoutDataType & { error?: string; },
 * >}
 */
const getCheckoutDataCloudFunction = httpsCallable(functions, 'getCheckoutData_v2');

// FUTURE: If the scriptMode is 100% safe to be removed in this file, please do so.

/**
 * @param {CatalogueItem[]} products
 * @return {boolean}
 */
const getIsConsulationOrBooking = (products) => [SERVICES.BOOKING_FEE, SERVICES.CONSULT].includes(products?.[0]?.name);

/**
 * @param {object} params
 * @param {boolean} params.termsRead
 * @param {boolean} params.isConsultationOrBooking
 * @param {boolean} params.scriptMode
 * @param {boolean} params.pharmacyConsentRead
 * @param {boolean} params.isPharmacyPending
 * @return {boolean}
 */
const getIsButtonDisabled = ({
  termsRead,
  isConsultationOrBooking,
  scriptMode,
  pharmacyConsentRead,
  isPharmacyPending,
}) =>
  (!termsRead && !isConsultationOrBooking) ||
  (scriptMode && !pharmacyConsentRead) ||
  (!scriptMode && isPharmacyPending);

/**
 * @param {object} params
 * @param {boolean} params.scriptMode
 * @param {CatalogueItem} params.product
 * @param {string} [params.description]
 * @param {number} params.quantity
 * @param {string} params.formCategory
 * @return {string}
 */
const getProductDetails = ({ scriptMode, product, description = '', quantity, formCategory }) => {
  const checkupLabel = formCategory === CHECKUP ? 'Checkup' : '';

  if (scriptMode) {
    return 'Script Admin Fee';
  }

  // For booking fee and consult, we return the description directly with the form type
  if ([SERVICES.BOOKING_FEE, SERVICES.CONSULT].includes(product?.name)) {
    return `${checkupLabel} ${description} (${getHealthCategoryDisplayName(product?.form)})`;
  }

  return `${getProductDetailedName({ productData: product })} x ${quantity}`;
};

const PREFIX = 'PaymentDetails';

const classes = {
  paymentDetails: `${PREFIX}-paymentDetails`,
  paymentItem: `${PREFIX}-paymentItem`,
  paymentTotal: `${PREFIX}-paymentTotal`,
};

const StyledGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  padding: '20px',

  [`& .${classes.paymentDetails}`]: {
    margin: '1rem 0',
    padding: '1rem 0',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    height: '100%',
    width: '100%',
    '@media (max-width: 600px)': {
      marginBottom: 0,
    },
  },

  [`& .${classes.paymentItem}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '3rem',
    '@media (max-width: 600px)': {
      marginBottom: '5px',
    },
  },

  [`& .${classes.paymentTotal}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0.5rem 0',
  },
}));

// Needs a thorough refactor
const PaymentDetails = () => {
  const {
    checkoutData,
    selectedTreatments,
    handlePaymentButton,
    isCheckoutLoaded,
    termsRead,
    description,
    setCheckoutData,
    scriptMode,
    formName,
    promoCode,
    pharmacyConsentRead,
    isPickup,
    isPharmacyPending,
    treatmentTranslator,
    setIsPromoLoading,
    showPaymentMethods,
  } = useContext(PaymentContext);
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const { discount, discountCode, appliedCredit, finalAmount, shippingCharge, gst, products, groupedProducts } =
    checkoutData || {};
  const isConsultationOrBooking = getIsConsulationOrBooking(products);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsPromoLoading(true);
    const getCheckoutData = async () => {
      try {
        const { data } = await getCheckoutDataCloudFunction({
          selectedTreatments: scriptMode ? [scriptPriceTranslator[formName]] : selectedTreatments,
          discountCode: promoCode,
          userId: user?.uid,
          isPickup,
          formCategory: formName,
        });

        if (data?.error) {
          throw new Error(data?.error);
        }

        setCheckoutData(data);
        setIsPromoLoading(false);
      } catch ({ message }) {
        setIsError(true);
        sentryCaptureMessage(message, { level: 'error', user: { selectedTreatments, scriptMode, formName } });
      }
    };
    if (selectedTreatments) {
      getCheckoutData();
    }
  }, [selectedTreatments, setCheckoutData, scriptMode, formName, promoCode, user, isPickup, setIsPromoLoading]);

  // Remove if the script admin fee will be reimplemented
  if (scriptMode) {
    return null;
  }

  if (isError) {
    return (
      <StyledGrid>
        <Typography fontWeight="bold" color="secondary">
          Oh snap there is an Error! Please refresh the page.
          <br />
          Sorry for the inconvenience.
        </Typography>
      </StyledGrid>
    );
  }

  return (
    <StyledGrid
      data-testid="payment-details-modal"
      size={{
        xs: 12,
        md: showPaymentMethods ? 6 : 12,
      }}
      sx={{
        '@media (max-width: 600px)': {
          paddingBottom: !showPaymentMethods ? 3 : 0,
        },
      }}
    >
      <Typography variant="h5" gutterBottom>
        Payment Details
      </Typography>
      {groupedProducts && Object.keys(treatmentTranslator || {}).length !== 0 && (
        <>
          <Box className={classes.paymentDetails}>
            {groupedProducts.map((groupedProduct) => {
              const { product, quantity, price, originalPrice, concession } = groupedProduct;

              const displayPrice = product.gst ? price / 1.1 : price;
              const displayOriginalPrice = product.gst ? originalPrice / 1.1 : originalPrice;

              // Check if MC promo was applied (backend adds these fields dynamically)
              /** @type {CatalogueItemWithPromo} */
              const productWithPromo = product;
              const hasMcPromo =
                productWithPromo.hasSpecialMcPromo === true &&
                typeof productWithPromo.originalPrice === 'number' &&
                productWithPromo.originalPrice > price;

              const productDetails = getProductDetails({
                scriptMode,
                product,
                description,
                quantity,
                formCategory: formName,
              });

              return (
                <Box key={product.id} className={classes.paymentItem}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography>{productDetails}</Typography>
                    {concession?.applied && (
                      <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
                        Concession pricing applied {concession.name ? `(${concession.name})` : ''}
                      </Typography>
                    )}
                  </Box>

                  {hasMcPromo ? (
                    <McPromoCheckoutDisplay
                      originalPrice={productWithPromo.originalPrice ?? price}
                      promoPrice={price}
                      quantity={quantity}
                      hasGst={product.gst || false}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      {concession?.applied && displayOriginalPrice > displayPrice && (
                        <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                          {convertToCurrency(displayOriginalPrice * quantity)}
                        </Typography>
                      )}

                      <Typography
                        fontWeight={concession?.applied ? 'bold' : 'regular'}
                        color={concession?.applied ? 'primary' : 'inherit'}
                      >
                        {convertToCurrency(displayPrice * quantity)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
            <Divider
              sx={{
                opacity: '0.6',
              }}
            />
            <Box className={classes.paymentItem}>
              <Typography>Shipping Fee</Typography>
              <Typography>{convertToCurrency(shippingCharge)}</Typography>
            </Box>
            <Divider />
            <Box className={classes.paymentItem}>
              <Typography>GST</Typography>
              <Typography>{convertToCurrency(gst || 0)}</Typography>
            </Box>
            {discount !== 0 && (
              <Box className={classes.paymentItem}>
                <Typography color="darkred">Discount Code: {discountCode}</Typography>
                <Typography color="darkred">{`- ${convertToCurrency(discount)}`}</Typography>
              </Box>
            )}
            <PromoContainer hasSpecialMcPromo={hasActiveMcPromo(groupedProducts.map((p) => p.product))} />
            <Divider />
            {appliedCredit !== 0 && (
              <>
                <Box className={classes.paymentItem}>
                  <Typography color="green">Credit To Be Applied</Typography>
                  <Typography color="green">{convertToCurrency(-appliedCredit)}</Typography>
                </Box>
                {parseInt(finalAmount, 10) === 1 && (
                  <Typography variant="caption" alignSelf="center" color="darkred">
                    Maximum discount applied, the rest of your credit can be used on the next purchase.
                  </Typography>
                )}
                <Divider
                  sx={{
                    opacity: '0.6',
                  }}
                />
              </>
            )}
            <Box className={classes.paymentTotal}>
              <Typography fontWeight="bold">Total</Typography>
              <Typography fontWeight="bold">{convertToCurrency(finalAmount)}</Typography>
            </Box>
          </Box>

          <TreatmentTerms
            isConsultationOrBooking={isConsultationOrBooking}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
          />

          {!isCheckoutLoaded && (
            <Box>
              <Button
                variant="contained"
                disabled={getIsButtonDisabled({
                  termsRead,
                  isConsultationOrBooking,
                  scriptMode,
                  pharmacyConsentRead,
                  isPharmacyPending,
                })}
                onClick={handlePaymentButton}
              >
                Proceed
              </Button>
            </Box>
          )}
        </>
      )}
      {!groupedProducts && <CircularProgress />}
    </StyledGrid>
  );
};

export default PaymentDetails;
